import { extractCreatePayloadType } from '@hyper-hyper-space/hhs3_mvt';
import { decode, encode } from './codec.js';
import { createDagProvider } from './provider.js';
import { createDagSynchronizer } from './synchronizer.js';
const REQUEST_TYPES = new Set([
    'header-request', 'payload-request', 'cancel-request',
]);
export function createSyncSession(target, swarms) {
    const activePeers = new Map();
    const issueCallbacks = [];
    function reportIssue(peerKey, issue) {
        for (const cb of issueCallbacks)
            cb(peerKey, issue);
    }
    function getPeers() {
        return [...activePeers.values()];
    }
    function sendTo(peer, msg) {
        if (!peer.channel.open)
            return 'closed';
        try {
            peer.channel.send(encode(msg));
            return 'sent';
        }
        catch (_a) {
            return 'error';
        }
    }
    function sendToWithReport(peer, msg) {
        const result = sendTo(peer, msg);
        if (result === 'closed')
            reportIssue(peer.key, 'send-closed');
        if (result === 'error')
            reportIssue(peer.key, 'send-error');
        return result;
    }
    const provider = createDagProvider(target.dag);
    const synchronizer = createDagSynchronizer(target.dagId, target.dag, target.rObject, target.hashSuite, getPeers, sendToWithReport, target.resolveRefDag);
    synchronizer.onPeerIssue((peerKey, issue) => {
        reportIssue(peerKey, issue);
    });
    function cleanupPeer(key) {
        if (!activePeers.has(key))
            return;
        activePeers.delete(key);
        provider.cancelPeer(key);
        synchronizer.removePeer(key);
    }
    function peerKeyOf(sp) {
        return `${sp.keyId}@${sp.endpoint}`;
    }
    function onPeerJoin(sp) {
        const key = peerKeyOf(sp);
        if (activePeers.has(key))
            return;
        const handle = { key, channel: sp.channel };
        activePeers.set(key, handle);
        sp.channel.onMessage((data) => {
            let msg;
            try {
                msg = decode(data);
            }
            catch (_a) {
                reportIssue(key, 'decode-failed');
                return;
            }
            if (msg.type === 'init-request' && msg.objectId === target.dagId) {
                target.dag.loadEntry(target.dagId).then(rootEntry => {
                    if (rootEntry !== undefined) {
                        const payloadType = extractCreatePayloadType(rootEntry.payload);
                        if (payloadType !== target.rObject.getType()) {
                            reportIssue(key, 'validation-failed');
                            return;
                        }
                        const resp = {
                            type: 'init-response',
                            objectId: target.dagId,
                            createPayload: rootEntry.payload,
                        };
                        sp.channel.send(encode(resp));
                    }
                });
                return;
            }
            if (REQUEST_TYPES.has(msg.type) || msg.type === 'new-frontier') {
                provider.handleMessage(msg, sp.channel);
            }
            synchronizer.handleMessage(msg, sp.channel);
        });
        sp.channel.onClose(() => {
            cleanupPeer(key);
        });
        synchronizer.addPeer(handle);
    }
    function onPeerLeave(sp) {
        const key = peerKeyOf(sp);
        cleanupPeer(key);
    }
    for (const swarm of swarms) {
        for (const existing of swarm.peers()) {
            onPeerJoin(existing);
        }
        swarm.onPeerJoin(onPeerJoin);
        swarm.onPeerLeave(onPeerLeave);
    }
    function destroy() {
        synchronizer.destroy();
        provider.destroy();
        activePeers.clear();
    }
    return {
        destroy,
        onPeerIssue: (cb) => { issueCallbacks.push(cb); },
        getDiagnostics: () => (Object.assign({ activePeerCount: activePeers.size }, synchronizer.getDiagnostics())),
    };
}
